import { loadSystems } from "../../data/loadSystems";
import { seededUnit } from "../../seededRandom";
import { safestRoute, shortestRoute } from "../../haulingMissions";
import { computeVisibility, scannerTierFromHullTier, type SystemVisibility } from "../../galaxyVisibility";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import type { HaulingRoute } from "../../haulingMissions";
import type { StarSystem } from "../../types";
import { posterFactionForSystem, standingTier } from "../../factionStanding";

// Space Map -- design doc Section 6's "10 Systems Flagged hiddenUntilScanned
// + Space Map UI" wireframe (LOCKED). A distinct fourth top-level screen
// (Section 2a: "not layered over the other three"), so it follows the
// Expedition View's own convention of a full-viewport `position: fixed`
// wrap rather than the shared renderHeader/#app 900px column -- a 107-node
// galaxy graph needs the room. Its own small "Exit" button substitutes for
// the shared header's nav.
//
// Real data used as-is: the full 107-system roster (src/data/systems.json),
// each system's real region/tier/dangerTier/hasStation/resources/
// connections, and shortestRoute()/safestRoute() (haulingMissions.ts) for
// the route line and hop count. Setting a waypoint reuses the exact same
// real state.waypointSystemId + triggerSave() mechanic Mission Diary's VIP
// missions already use -- the header's existing "Travel" button (Mission
// Board/Diary) completes it, so no second travel mechanism was invented.
//
// Scanner-tier gating is real now (16.46, galaxyVisibility.ts): scanner
// tier = the current ship's real hull tier (T1-T4 -> 1-4, no separate
// stat/module exists or is invented). The real three-state visibility
// model (hidden / visible-unscanned / fully-scanned) is computed fresh
// every render from state.visitedSystemIds + the current ship + real
// per-system dangerTier -- nothing about visibility itself is persisted,
// only the fact of having visited a system. The 10 real hiddenUntilScanned
// endgame systems (HIDDEN_SYSTEM_IDS) get their own real neighbor-gate-scan
// discovery too (wired into the Expedition View's SCAN button,
// expeditionScreen.ts's doScan()) -- a detected-but-unvisited one shows
// here as a 4th "signal" state: a dashed marker with no name or stats,
// connected by one edge to whichever of its real neighbors is itself
// visible (a deliberate simplification -- detection is tracked per hidden
// system, not per detecting neighbor, so if a 2-neighbor hidden system
// happens to have both neighbors visible, both edges show rather than
// only the one that actually detected it; harmless since 8 of the 10 have
// only one real neighbor anyway).
//
// Two of the doc's three route variants are real: fewest hops
// (shortestRoute) and safest (safestRoute, minimizes the highest danger
// tier touched along the route). The third, "most fuel-efficient," was
// **CUT from the design in 16.76** -- not pending, not blocked. Two route
// choices are enough of a decision, and a fuel-weighted one mostly
// correlates with fewest-hops anyway. (Its original justification, "no
// fuel mechanic to weight by," had also expired when Fuel shipped in
// 16.50/16.65 -- the removal is a deliberate call, not that gap.)
//
// What's NOT real yet, flagged rather than faked:
// - **System (x, y) positions are generated, not real/curated.** No
//   coordinate field exists in the data -- only the graph topology and
//   region/tier labels are real. Positions are deterministic (hashed from
//   each system's own id, not Math.random(), so the map doesn't reshuffle
//   between renders) and placed into hand-picked region clusters that
//   match Section 6's qualitative topology description (small central Safe
//   Zone, a neutral ring buffering it, three faction territories spread
//   around the rim, pirate pockets scattered further out) -- not a real
//   layout algorithm or curated per-system coordinates.
// - **Faction standing and Bases used/slots** aren't tracked anywhere in
//   state -- the info panel omits them rather than showing an invented
//   number, same as the Expedition View's own "n/a (not modeled)"
//   convention for Thrust/Fuel. "Scanned ratio" is real now, though
//   (visible-and-scanned systems / visible systems total).

const systems = loadSystems();
const systemsById = new Map(systems.map((s) => [s.id, s]));

interface Vec2 {
  x: number;
  y: number;
}

// Positions must stay stable across re-renders within a session
// (selecting a system, changing zoom, etc. re-runs this module's render
// function, and a reshuffling map would be disorienting) -- seededUnit
// (src/seededRandom.ts) is what makes that deterministic.

interface RegionCluster {
  cx: number;
  cy: number;
  rMin: number;
  rMax: number;
  color: string;
}

// Hand-placed cluster geometry matching Section 6's described topology --
// see file header. Colors: red/blue/yellow for the 3 factions per the
// doc's own "color-coded red/blue/yellow for map purposes" line; safe/
// neutral get calmer neutral tones; pirates get a distinct purple (no
// color is specified for them in the doc, and purple isn't used as a
// semantic color anywhere else in the app).
const REGION_CLUSTERS: Record<StarSystem["region"], RegionCluster> = {
  safe: { cx: 0, cy: 0, rMin: 0, rMax: 90, color: "#8fa6c9" },
  neutral: { cx: 0, cy: 0, rMin: 220, rMax: 300, color: "#7d8ba0" },
  crimson: { cx: -430, cy: 340, rMin: 0, rMax: 210, color: "#c0392b" },
  azure: { cx: 430, cy: 340, rMin: 0, rMax: 210, color: "#3aa0e8" },
  solthane: { cx: 0, cy: -520, rMin: 0, rMax: 210, color: "#d9b23f" },
  pirate: { cx: 0, cy: 0, rMin: 620, rMax: 760, color: "#8a5fd6" }
};

function computePosition(system: StarSystem): Vec2 {
  const cluster = REGION_CLUSTERS[system.region];
  const angle = seededUnit(system.id + "a") * Math.PI * 2;
  const r = cluster.rMin + seededUnit(system.id + "b") * (cluster.rMax - cluster.rMin);
  return { x: cluster.cx + Math.cos(angle) * r, y: cluster.cy + Math.sin(angle) * r };
}

const positions = new Map<string, Vec2>(systems.map((s) => [s.id, computePosition(s)]));

// Node radius by tier (real field) -- a stand-in for Section 6's "node
// size encodes system significance (planets/scannable locations)" bullet,
// since no such count exists in the data; tier is the closest real proxy
// (hub systems are the most developed/significant).
function nodeRadius(system: StarSystem): number {
  if (system.tier === "hub") return 9;
  if (system.tier === "standard") return 6;
  if (system.tier === "pocket-anchor") return 6;
  return 4; // frontier, pocket-member
}

type ZoomLevel = "full" | "region" | "close";
type RouteVariant = "hops" | "safe";

function boundingViewBox(points: Vec2[], padding: number): string {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - padding;
  const minY = Math.min(...ys) - padding;
  const maxX = Math.max(...xs) + padding;
  const maxY = Math.max(...ys) + padding;
  return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
}

function viewBoxForZoom(zoom: ZoomLevel, focusId: string): string {
  const focus = positions.get(focusId) ?? { x: 0, y: 0 };
  if (zoom === "close") {
    return `${focus.x - 260} ${focus.y - 260} 520 520`;
  }
  if (zoom === "region") {
    const region = systemsById.get(focusId)!.region;
    const clusterIds = systems.filter((s) => s.region === region).map((s) => s.id);
    return boundingViewBox(
      clusterIds.map((id) => positions.get(id)!),
      120
    );
  }
  return boundingViewBox([...positions.values()], 60);
}

export function renderSpaceMapScreen(container: HTMLElement, ctx: ScreenContext): void {
  let zoom: ZoomLevel = "full";
  // Opens on the standing waypoint when there is one (16.98, Don's report).
  // Waypoints can now be set from three screens that are not this one -- the
  // Market's Highest Demand column, Galaxy Prices, and the Cargo hold -- and
  // arriving here with nothing selected meant the course the captain had just
  // plotted was invisible: no route line, no highlighted node, no info panel.
  let selectedId: string | null = state.waypointSystemId;
  let toast: string | null = null;
  let routeVariant: RouteVariant = "hops";

  const wrap = document.createElement("div");
  wrap.className = "sm-wrap";

  function render(): void {
    // Scanner tier = the current ship's real hull tier (16.46) -- no
    // separate scanner stat/module exists or is invented. Falls back to
    // T1 if there's somehow no ship (shouldn't happen -- every top-level
    // View already requires one).
    const scannerTier = scannerTierFromHullTier(state.currentShip?.hullTier ?? "T1");
    const visibility = computeVisibility(systems, state.visitedSystemIds, state.detectedHiddenSystemIds, scannerTier);
    const visibleSystems = systems.filter((s) => visibility.get(s.id) !== "hidden");

    const focusId = selectedId ?? state.currentSystemId;
    const viewBox = viewBoxForZoom(zoom, focusId);
    const selected = selectedId ? systemsById.get(selectedId) ?? null : null;
    const selectedVis: SystemVisibility | undefined = selectedId ? visibility.get(selectedId) : undefined;

    const scannedCount = visibleSystems.filter((s) => visibility.get(s.id) === "scanned").length;

    wrap.innerHTML = `
      <div class="sm-topbar">
        <button type="button" class="sm-exit" data-exit>&lsaquo; Exit to Mission Board</button>
        <span class="dim sm-scanner-line">Scanner Tier ${scannerTier} &middot; ${scannedCount}/${visibleSystems.length} scanned</span>
        <input type="text" class="sm-search" placeholder="Search systems by name..." data-search />
        <div class="sm-zoom" data-zoom>
          <div data-z="full" class="${zoom === "full" ? "active" : ""}">Full</div>
          <div data-z="region" class="${zoom === "region" ? "active" : ""}">Region</div>
          <div data-z="close" class="${zoom === "close" ? "active" : ""}">Close</div>
        </div>
      </div>
      <div class="sm-search-results" data-search-results></div>

      <svg class="sm-svg" viewBox="${viewBox}" data-svg>
        <g data-clouds>
          ${Object.entries(REGION_CLUSTERS)
            .map(
              ([region, c]) =>
                `<circle cx="${c.cx}" cy="${c.cy}" r="${c.rMax + 40}" fill="${c.color}" fill-opacity="0.06" stroke="${c.color}" stroke-opacity="0.15" data-region="${region}"></circle>`
            )
            .join("")}
        </g>
        <g data-edges stroke="#2a3a4d" stroke-width="1">
          ${edgesHtml(visibility)}
        </g>
        ${waypointRouteHtml(selectedId)}
        ${selected && selectedVis !== "signal" && selected.id !== state.currentSystemId ? routeLineHtml(state.currentSystemId, selected.id, routeVariant) : ""}
        <g data-nodes>
          ${visibleSystems.map((s) => nodeHtml(s, s.id === selectedId, visibility.get(s.id)!)).join("")}
        </g>
        ${youAreHereHtml(state.currentSystemId)}
      </svg>

      <div class="sm-legend">
        <span><span class="sm-leg-dot" style="background:#f0c94a;border:2px solid #fff"></span>You are here</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.safe.color}"></span>Safe</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.neutral.color}"></span>Neutral</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.crimson.color}"></span>Crimson Concord</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.azure.color}"></span>Azure Compact</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.solthane.color}"></span>Solthane Accord</span>
        <span><span class="sm-leg-dot" style="background:${REGION_CLUSTERS.pirate.color}"></span>Pirate pocket</span>
        <span><span class="sm-leg-dot sm-leg-signal"></span>Unconfirmed signal</span>
        ${
          state.waypointSystemId
            ? `<span><span class="sm-leg-dot sm-leg-waypoint"></span>Waypoint &amp; course</span>`
            : ""
        }
      </div>

      <div class="sm-panel">
        ${selected ? infoPanelHtml(selected, selectedVis!, routeVariant) : '<p class="dim">Select a system to see its details.</p>'}
      </div>

      ${toast ? `<div class="sm-toast">${toast}</div>` : ""}
    `;

    wrap.querySelector<HTMLButtonElement>("[data-exit]")!.addEventListener("click", () => {
      ctx.navigate("board");
    });

    wrap.querySelectorAll<HTMLElement>("[data-zoom] div[data-z]").forEach((el) => {
      el.addEventListener("click", () => {
        zoom = el.dataset.z as ZoomLevel;
        render();
      });
    });

    wrap.querySelector<HTMLElement>("[data-svg]")!.addEventListener("click", (ev) => {
      const node = (ev.target as HTMLElement).closest<SVGElement>("[data-sys-id]");
      if (node) {
        selectedId = node.dataset.sysId!;
        render();
      }
    });

    const searchInput = wrap.querySelector<HTMLInputElement>("[data-search]")!;
    const searchResults = wrap.querySelector<HTMLElement>("[data-search-results]")!;
    searchInput.addEventListener("input", () => {
      const q = searchInput.value.trim().toLowerCase();
      if (!q) {
        searchResults.innerHTML = "";
        return;
      }
      // Search only ever surfaces systems the captain can actually
      // perceive (16.46) -- a hidden or unconfirmed-signal system can't be
      // found by typing its real name, since that name isn't known yet.
      const matches = visibleSystems
        .filter((s) => visibility.get(s.id) === "scanned" || visibility.get(s.id) === "unscanned")
        .filter((s) => s.name.toLowerCase().includes(q))
        .slice(0, 8);
      searchResults.innerHTML = matches
        .map((s) => `<div class="sm-search-item" data-goto="${s.id}">${s.name} <span class="dim">(${s.region})</span></div>`)
        .join("");
      searchResults.querySelectorAll<HTMLElement>("[data-goto]").forEach((el) => {
        el.addEventListener("click", () => {
          selectedId = el.dataset.goto!;
          zoom = "close";
          searchInput.value = "";
          render();
        });
      });
    });

    wrap.querySelectorAll<HTMLButtonElement>("[data-route-variant]").forEach((btn) => {
      btn.addEventListener("click", () => {
        routeVariant = btn.dataset.routeVariant as RouteVariant;
        render();
      });
    });

    const waypointBtn = wrap.querySelector<HTMLButtonElement>("[data-set-waypoint]");
    waypointBtn?.addEventListener("click", () => {
      if (!selected) return;
      state.waypointSystemId = selected.id;
      triggerSave(ctx);
      toast = `Waypoint set: ${selected.name}. Use "Travel" on the Mission Board/Diary header to jump there.`;
      render();
    });

    const clearBtn = wrap.querySelector<HTMLButtonElement>("[data-clear-waypoint]");
    clearBtn?.addEventListener("click", () => {
      state.waypointSystemId = null;
      triggerSave(ctx);
      toast = "Waypoint cleared.";
      render();
    });
  }

  // Only draws an edge when both endpoints are actually perceivable
  // (16.46) -- a "signal" system draws just the one edge to whichever
  // real neighbor(s) of it are themselves visible (see file header for
  // why this can occasionally show 2 edges instead of the doc's literal
  // "the system it was scanned from").
  function edgesHtml(visibility: Map<string, SystemVisibility>): string {
    const drawn = new Set<string>();
    const lines: string[] = [];
    systems.forEach((s) => {
      if (visibility.get(s.id) === "hidden") return;
      const from = positions.get(s.id)!;
      s.connections.forEach((destId) => {
        if (visibility.get(destId) === "hidden") return;
        const key = [s.id, destId].sort().join("-");
        if (drawn.has(key)) return;
        drawn.add(key);
        const to = positions.get(destId);
        if (!to) return;
        const dashed = visibility.get(s.id) === "signal" || visibility.get(destId) === "signal";
        lines.push(`<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}"${dashed ? ' stroke-dasharray="4 4"' : ""}></line>`);
      });
    });
    return lines.join("");
  }

  function routeToward(fromId: string, toId: string, variant: RouteVariant): HaulingRoute {
    return variant === "safe" ? safestRoute(fromId, toId, systems) : shortestRoute(fromId, toId, systems);
  }

  function routeLineHtml(fromId: string, toId: string, variant: RouteVariant): string {
    const route = routeToward(fromId, toId, variant);
    const pts = route.path.map((id) => positions.get(id)!);
    const points = pts.map((p) => `${p.x},${p.y}`).join(" ");
    return `<polyline points="${points}" fill="none" stroke="#f0c94a" stroke-width="3" stroke-opacity="0.85" data-route></polyline>`;
  }

  /**
   * The standing course, drawn whatever is selected (16.98).
   *
   * Selecting a different system to compare it against must not erase the route
   * you already committed to -- so the waypoint keeps its own dashed line and its
   * own ring underneath the solid selection route. Skipped when the waypoint IS
   * the selection, since `routeLineHtml` is already drawing that exact path and
   * two lines on identical points render as one smudge.
   */
  function waypointRouteHtml(currentSelectionId: string | null): string {
    const id = state.waypointSystemId;
    if (!id || id === state.currentSystemId || id === currentSelectionId) return "";
    if (!positions.has(id)) return "";
    const pts = routeToward(state.currentSystemId, id, routeVariant).path.map((p) => positions.get(p)!);
    const p = positions.get(id)!;
    return `
      <polyline points="${pts.map((q) => `${q.x},${q.y}`).join(" ")}" fill="none"
                stroke="#f0c94a" stroke-width="2" stroke-opacity="0.5"
                stroke-dasharray="7 6" data-waypoint-route></polyline>
      <circle cx="${p.x}" cy="${p.y}" r="13" fill="none" stroke="#f0c94a"
              stroke-width="2" stroke-opacity="0.75" stroke-dasharray="4 4" data-waypoint-ring></circle>`;
  }

  function nodeHtml(s: StarSystem, isSelected: boolean, vis: SystemVisibility): string {
    const p = positions.get(s.id)!;
    const r = nodeRadius(s);
    if (vis === "signal") {
      // Section 6: "a distinct 'signal detected, unvisited' marker
      // (dashed outline, no stats)" -- no name label, no real color
      // (identity genuinely isn't known yet), just a dim dashed ring.
      return `
        <g data-sys-id="${s.id}" class="sm-node sm-node-signal${isSelected ? " selected" : ""}">
          <circle cx="${p.x}" cy="${p.y}" r="${r}" fill="none" stroke="#f0c94a" stroke-width="${isSelected ? 2.5 : 1.5}" stroke-dasharray="3 3"></circle>
        </g>
      `;
    }
    const color = REGION_CLUSTERS[s.region].color;
    const showLabel = zoom !== "full" || s.tier === "hub";
    const dim = vis === "unscanned";
    return `
      <g data-sys-id="${s.id}" class="sm-node${isSelected ? " selected" : ""}${dim ? " sm-node-unscanned" : ""}">
        <circle cx="${p.x}" cy="${p.y}" r="${r}" fill="${color}" stroke="${isSelected ? "#f0c94a" : "#04070d"}" stroke-width="${isSelected ? 3 : 1}"></circle>
        ${showLabel ? `<text x="${p.x + r + 4}" y="${p.y + 3}" class="sm-node-label">${s.name}</text>` : ""}
      </g>
    `;
  }

  function youAreHereHtml(currentId: string): string {
    const p = positions.get(currentId);
    if (!p) return "";
    return `<circle cx="${p.x}" cy="${p.y}" r="16" fill="none" stroke="#f0c94a" stroke-width="2.5" data-here></circle>`;
  }

  function infoPanelHtml(s: StarSystem, vis: SystemVisibility, variant: RouteVariant): string {
    if (vis === "signal") {
      return `
        <h2>Unconfirmed Signal</h2>
        <p class="dim">
          A jump gate was detected here, but nothing about the system beyond it is known yet --
          scanning revealed the gate itself, not what's on the other side.
          Jump through from a neighboring system to reveal it.
        </p>
      `;
    }

    const isHere = s.id === state.currentSystemId;
    // Routing only ever needs real connectivity data (position/graph
    // edges), which is already known for "unscanned" too -- only the
    // per-system STAT block below actually needs a full scan/visit.
    const route = isHere ? null : routeToward(state.currentSystemId, s.id, variant);
    const isWaypoint = state.waypointSystemId === s.id;

    const statsHtml =
      vis === "unscanned"
        ? `
            <div class="sm-panel-row"><span>Danger Tier</span><span class="dim">unknown -- scan or visit to learn more</span></div>
            <div class="sm-panel-row"><span>Station</span><span class="dim">unknown -- scan or visit to learn more</span></div>
            <div class="sm-panel-row"><span>Resources</span><span class="dim">unknown -- scan or visit to learn more</span></div>
            <p class="dim">Visible from real scanner range, not yet visited in person (16.46). Travel there to fully scan it.</p>
          `
        : `
            <div class="sm-panel-row"><span>Tier</span><span>${s.tier}</span></div>
            <div class="sm-panel-row"><span>Danger Tier</span><span>${s.dangerTierLabel} (${s.dangerTier})</span></div>
            <div class="sm-panel-row"><span>Station</span><span>${s.hasStation ? "Yes" : "No"}</span></div>
            <div class="sm-panel-row"><span>${s.planets.length > 1 ? "Planets" : "Planet"}</span><span>${s.planets.map((p) => `${p.name} (${p.planetType})`).join(", ")}</span></div>
            <div class="sm-panel-row"><span>Resources</span><span>${s.resources.join(", ")}</span></div>
            <div class="sm-panel-row"><span>Faction standing</span><span>${factionStandingLabel(s)}</span></div>
            <div class="sm-panel-row"><span>Bases</span><span class="dim">not modeled yet</span></div>
          `;

    return `
      <h2>${s.name}${isHere ? ' <span class="dim">(current)</span>' : ""}</h2>
      <div class="sm-panel-row"><span>Region</span><span>${regionLabel(s.region)}</span></div>
      <div class="sm-panel-row"><span>Connections</span><span>${s.connections.length}</span></div>
      ${statsHtml}
      ${
        !isHere
          ? `<div class="sm-panel-row">
               <span>Route</span>
               <span>
                 <button type="button" class="pill-btn ${variant === "hops" ? "active" : ""}" data-route-variant="hops">Fewest hops</button>
                 <button type="button" class="pill-btn ${variant === "safe" ? "active" : ""}" data-route-variant="safe">Safest</button>
               </span>
             </div>`
          : ""
      }
      ${
        route
          ? `<div class="sm-panel-row"><span>${variant === "safe" ? "Route (safest)" : "Route (fewest hops)"}</span><span>${route.hops} hop${route.hops === 1 ? "" : "s"}, max Danger Tier ${route.maxDangerTier} en route</span></div>`
          : ""
      }
      <div class="sm-panel-actions">
        ${
          !isHere
            ? `<button type="button" data-set-waypoint>${isWaypoint ? "Waypoint set ✓" : "Set Waypoint"}</button>`
            : ""
        }
        ${isWaypoint ? `<button type="button" class="sm-btn-secondary" data-clear-waypoint>Clear Waypoint</button>` : ""}
      </div>
    `;
  }

  render();
  container.appendChild(wrap);
}

function regionLabel(region: StarSystem["region"]): string {
  switch (region) {
    case "safe":
      return "Safe Zone (Sanctuary)";
    case "neutral":
      return "Neutral Corridor";
    case "crimson":
      return "Crimson Concord";
    case "azure":
      return "Azure Compact";
    case "solthane":
      return "Solthane Accord";
    case "pirate":
      return "Pirate pocket";
  }
}

// Faction Standing (16.48/16.55) -- this System's own poster faction (its
// region), the captain's current numeric standing with them, and which of
// the three tiers that lands in. null for safe/neutral space, which posts
// no faction-tagged missions and so has no standing to show.
function factionStandingLabel(system: StarSystem): string {
  const faction = posterFactionForSystem(system);
  if (!faction) {
    return "Unaffiliated";
  }
  const value = state.standing[faction];
  return `${faction} ${value.toFixed(1)} (${standingTier(value)})`;
}
